import { isNil } from "lodash";
import { constants } from "../../constants";
import { numericUtils } from "../../utils";
import { FilterRadioGroup, FilterRangeGroup, FilterType } from "../filters/FilterGroup";
import { IDeserializeCommand } from "../../utils/filtering/serializers/DeserializeCommand";
import moment from "moment";

export enum FilterPeriodCommonType {
    All = "All",
    Yes = "Yes",
    No = "No",
    Custom = "Custom",
}

export enum OutOfNCFilterPeriod {
    Range0_3M = "0-3M",
    Range3M_6M = "3M-6M",
    Range6M_12M = "6M-12M",
    Range12M_24M = "12M-24M"
}

export type OutOfNCPeriodFilterType = FilterPeriodCommonType | OutOfNCFilterPeriod;

export enum OutOfRIFilterPeriod {
    Range0_6M = "0-6M",
    Range6M_12M = "6M-12M",
    Range12M_18M = "12M-18M",
    Range105Y_205Y = "1.5Y-2.5Y",
    Range205Y_305Y = "2.5Y-3.5Y",
    Range305Y_5Y = "3.5Y-5Y"
}

export type OutOfRIPeriodFilterType = FilterPeriodCommonType | OutOfRIFilterPeriod;

export function convertFilterPeriodToYearsRange(period: OutOfNCPeriodFilterType | OutOfRIPeriodFilterType) {
    const [fromPart, toPart] = period.split('-');

    if (!fromPart || !toPart) return null;

    const convertToNumber = (value: string) => Number(value.replace(/[^0-9.]/g, ""));

    const from = convertToNumber(fromPart)
    const to = convertToNumber(toPart)

    const isMonth = toPart.endsWith('M');

    return {
        from: isMonth ? numericUtils.round(from / 12, 2) : from,
        to: isMonth ? numericUtils.round(to / 12, 2) : to,
    }
}

export function convertPeriodEndToBoolean(value?: OutOfNCPeriodFilterType | OutOfRIPeriodFilterType | null) {
    if (value === FilterPeriodCommonType.Yes) return true
    if (value === FilterPeriodCommonType.No) return false
    return undefined
}

export function convertPeriodEndToYearsRange(
    option?: OutOfNCPeriodFilterType | OutOfRIPeriodFilterType | null,
    customRangeFrom?: string | number,
    customRangeTo?: string | number) {
    if (!option ||
        option === FilterPeriodCommonType.All ||
        option === FilterPeriodCommonType.Yes ||
        option === FilterPeriodCommonType.No
    ) return undefined;

    const isCustom =
        option === FilterPeriodCommonType.Custom &&
        (customRangeFrom != null || customRangeTo != null)
    if (isCustom) {
        return {
            from: numericUtils.isNumber(customRangeFrom) ? numericUtils.numberOrDefault(customRangeFrom) : undefined,
            to: numericUtils.isNumber(customRangeTo) ? numericUtils.numberOrDefault(customRangeTo) : undefined
        }
    }

    return convertFilterPeriodToYearsRange(option);
}

export function calculatePeriodEndYears(periodEnd?: Date) {
    if (!periodEnd) return undefined;

    const today = moment.utc().startOf('day');
    const end = moment.utc(periodEnd).startOf('day');
    return numericUtils.round(end.diff(today, 'day', true) / 365, 2);
}

export const outOfNC: FilterRadioGroup<OutOfNCPeriodFilterType> = {
    key: 'outOfNC',
    groupKey: 'outOfNC',
    type: FilterType.Radio,
    visible: false,
    text: 'Out of NC',
    disabled: false,
    selected: false,
    isApplied: true,
    filter: {
        selectedOption: null,
        options: [{
            value: FilterPeriodCommonType.All,
            text: 'All',
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: FilterPeriodCommonType.Yes,
            text: 'Yes',
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: FilterPeriodCommonType.No,
            text: 'No',
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: OutOfNCFilterPeriod.Range0_3M,
            text: "0 — 3M",
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: OutOfNCFilterPeriod.Range3M_6M,
            text: "3M — 6M",
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: OutOfNCFilterPeriod.Range6M_12M,
            text: "6M — 12M",
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: OutOfNCFilterPeriod.Range12M_24M,
            text: "12M — 24M",
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: FilterPeriodCommonType.Custom,
            text: "Custom",
            selected: false,
            visible: true,
            disabled: false
        }]
    }
};

export const outOfNCRange: FilterRangeGroup = {
    key: 'outOfNCRange',
    groupKey: 'outOfNC',
    type: FilterType.Range,
    visible: false,
    text: 'Out of NC',
    disabled: false,
    selected: false,
    isApplied: true,
    decimals: 2,
    filter: { from: '', to: '', minValue: constants.filterPeriodEndRange.min.toString(), maxValue: constants.filterPeriodEndRange.max.toString() },
}

export const outOfRI: FilterRadioGroup<OutOfRIPeriodFilterType> = {
    key: 'outOfRI',
    groupKey: 'outOfRI',
    type: FilterType.Radio,
    visible: false,
    text: 'Out of RI',
    disabled: false,
    selected: false,
    isApplied: true,
    filter: {
        selectedOption: null,
        options: [{
            value: FilterPeriodCommonType.All,
            text: 'All',
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: FilterPeriodCommonType.Yes,
            text: 'Yes',
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: FilterPeriodCommonType.No,
            text: 'No',
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: OutOfRIFilterPeriod.Range0_6M,
            text: "0 — 6M",
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: OutOfRIFilterPeriod.Range6M_12M,
            text: "6M — 12M",
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: OutOfRIFilterPeriod.Range12M_18M,
            text: "12M — 18M",
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: OutOfRIFilterPeriod.Range105Y_205Y,
            text: "1.5Y — 2.5Y",
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: OutOfRIFilterPeriod.Range205Y_305Y,
            text: "2.5Y — 3.5Y",
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: OutOfRIFilterPeriod.Range305Y_5Y,
            text: "3.5Y — 5Y",
            selected: false,
            visible: true,
            disabled: false
        }, {
            value: FilterPeriodCommonType.Custom,
            text: "Custom",
            selected: false,
            visible: true,
            disabled: false
        }]
    }
};

export const outOfRIRange: FilterRangeGroup = {
    key: 'outOfRIRange',
    groupKey: 'outOfRI',
    type: FilterType.Range,
    visible: false,
    text: 'Out of RI',
    disabled: false,
    selected: false,
    isApplied: true,
    decimals: 2,
    filter: { from: '', to: '', minValue: constants.filterPeriodEndRange.min.toString(), maxValue: constants.filterPeriodEndRange.max.toString() },
}

export function createPeriodEndCommands<T extends OutOfNCPeriodFilterType | OutOfRIPeriodFilterType>(
    canExecute: boolean,
    outOfPeriodType?: T,
    customPeriodRangeFrom?: number,
    customPeriodRangeTo?: number,
): IDeserializeCommand[] {
    const isOutOfNCRangeValid =
        (!isNil(customPeriodRangeFrom) || !isNil(customPeriodRangeTo)) &&
        (isNil(customPeriodRangeFrom) || isNil(customPeriodRangeTo) || +customPeriodRangeFrom <= +customPeriodRangeTo)

    const periodCommand = {
        canExecute: () => canExecute,
        shouldExecute: () => !!outOfPeriodType && (outOfPeriodType !== FilterPeriodCommonType.Custom || isOutOfNCRangeValid),
        execute: () => null
    };

    const periodRangeRangeCommand = {
        canExecute: () => canExecute,
        shouldExecute: () => outOfPeriodType === FilterPeriodCommonType.Custom && isOutOfNCRangeValid,
        execute: () => null
    };

    return [periodCommand, periodRangeRangeCommand];
}